(ns practitest.frontend.et.views
  (:refer-clojure :exclude [parse-boolean])
  (:require
    [reagent.core :as reagent]
    [re-frame.core :as re-frame]
    [clojure.string :as string]
    [practitest.frontend.components.markdown :as markdown]
    [practitest.frontend.common.attachment-preview :refer [build-preview]]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common-old-fe.ui :as ui]
    [practitest.frontend.common.tooltip :as common-tooltip]
    [practitest.frontend.common-old-fe.logging :as log]
    [practitest.frontend.common-old-fe.utils :as utils]
    [practitest.frontend.common-old-fe.i18n :as i18n]
    [practitest.frontend.components.datepicker :as datepicker]
    [practitest.frontend.components.datepicker2 :as datepicker2]
    [practitest.frontend.import-ticket-modal.main :as import-ticket]
    [practitest.frontend.import-ticket-modal.events :as import-ticket-events]
    [practitest.frontend.common.keyboard-shortcuts :as keyboard-shortcuts]
    [practitest.frontend.issue.bugtracker :as bugtracker]
    [practitest.frontend.et.events :as events]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.common.bugtrackers :as bugtrackers]
    ["react-select" :default Select :refer [components]]))

(defn dirty? [entity]
  (boolean (or (seq (:dirty-fields entity))
               (seq (:annotations-to-delete entity)))))

(defn humanize-size [bytes]
  (str (.toFixed (/ bytes 1024) 2) "k"))

(defn event->value [event]
  (-> event .-target .-value))

(defn parse-boolean [x]
  (if (string? x)
    (= "true" (string/lower-case x))
    (boolean x)))

(defn field-label [mandatory? label]
  [:label.form-label label (when mandatory? [:span.text-danger " (*)"])])

(defn uglify-styles []
  #js {:control (fn [base]
                  (js/Object.assign
                    (clj->js base)
                    #js {:width "100%"
                         :padding "0px"}))
       :valueContainer (fn [base]
                         (js/Object.assign
                           (clj->js base)
                           #js {:padding "0px 8px"}))
       :menu (fn [base]
               (js/Object.assign
                 (clj->js base)
                 #js {:border "1px solid var(--pt-color-border-light)"
                      :width "90%"
                      :z-index "3"}))
       :dropdownIndicator (fn [base]
                            (js/Object.assign
                              (clj->js base)
                              #js {:padding 2}))})

(defn et-select [options value props on-change]
  (fn [options value _ on-change]
    (let []
      [:> Select
       {:className   "col-12 et-select"
        :styles      (uglify-styles)
        :placeholder (:placeholder props)
        :value       (if (:multi? props)
                       (clj->js (map (fn [v] {:value v :label v}) value))
                       (clj->js value))
        :isClearable (:clearable? props)
        :isMulti     (:multi? props)
        :options     (vec options)
        :onChange    #(on-change %)}])))

(defn list-field [id possible-values default-value value]
  (let [values (map (fn [v]
                      {:value (:id v) :label (:name v)}) possible-values)
        value (if (string? value)
                {:value (:value (first (filter #(= (:label %) value) values))) :label value}
                value)
        state (reagent/atom (or value default-value ""))
        props {:placeholder "Select ..."
               :clearable?  true
               :multi?      false}
        on-change #(re-frame/dispatch [:et/set-field
                                       (keyword id)
                                       (:label (js->clj % :keywordize-keys true))])]
    [et-select values @state props on-change]))

(defn user-field [id possible-values default-value value]
  (let [values (map (fn [v]
                      {:value (:id v) :label (:name v)}) possible-values)
        value (if (or (number? value) (string? value))
                {:value value :label (:label (last (filter #(= (:value %) (js/parseInt value)) values)))}
                value)
        state (reagent/atom (or value default-value ""))
        props {:placeholder "Select ..."
               :clearable?  true
               :multi?      false}
        on-change #(re-frame/dispatch [:et/set-field
                                       (keyword id)
                                       (:value (js->clj % :keywordize-keys true))])]
    [et-select values @state props on-change]))

(defn valid-file? [file]
  (let [max-file-size @(re-frame/subscribe [:et/max-attachment-size])]
    (<= (.-size file) (* max-file-size 1024 1024))))

(defn attach-file [file annotation-position file-placeholder]
  (let [reader (js/FileReader.)]
    (set! (.-onload reader)
          (fn [e]
            (re-frame/dispatch [:et/add-annotation-attachment
                                annotation-position
                                {:file file
                                 :name (.-name file)
                                 :size (.-size file)
                                 :type (.-type file)
                                 :data (-> e .-target .-result)
                                 :description-file-placeholder file-placeholder}])))
    (.readAsDataURL reader file)))

(defn report-invalid-file [file]
  (log/warn (i18n/t :invalid_file) file))

(defn process-file [file annotation-position file-placeholder]
  (if (valid-file? file)
    (attach-file file annotation-position file-placeholder)
    (report-invalid-file file)))

(defn markdown-container [placeholder mandatory? subscription set-value-event position autosize?]
  (fn []
    [:div.col-12.row
     {:style {:border "1px solid var(--pt-color-border-light)"
              :border-radius "10px"}}
     [markdown/markdown-editor
      {:class (dom/classes-pairs :description false :px-0 true)
       :value @(re-frame/subscribe subscription)
       :autosize? autosize?
       :read-only? false
       :hide? true
       :on-change #(re-frame/dispatch-sync (set-value-event %))
       :on-load-file (fn [file file-placeholder] (process-file file position file-placeholder))}]]))

(defn et-memo-field [label placeholder mandatory? subscription set-value-event]
  (let [state (reagent/atom nil)
        markdown-support? (re-frame/subscribe [:et/markdown-support?])
        rtl? (re-frame/subscribe [:et/rtl?])]
    (fn []
      [:div.col-6.form-group
       [field-label mandatory? label]
       (if markdown-support?
         [markdown-container placeholder mandatory? subscription set-value-event nil true]
         [:textarea.form-control
          (cond-> {:rows          5
                   :cols          20
                   :default-value @(re-frame/subscribe subscription)
                   :ref           #(reset! state %)
                   :placeholder   placeholder
                   :on-change     #(re-frame/dispatch [set-value-event (.-value @state)])}
            @rtl? (assoc :dir "rtl"))])])))

(defn process-files [annotation-position files]
  (doseq [file (array-seq files)]
    (process-file file annotation-position nil)))

(defn dropzone [position]
  (let [file-input-id (str (i18n/t :file) position)]
    [:div.well.et-annotation-attachment-dropzone.drop_zone
     {:on-drag-over #(.preventDefault %)
      :on-drop      (fn [e]
                      (.preventDefault e)
                      (process-files position (-> e .-dataTransfer .-files)))
      :on-click     (fn [e]
                      (.click (js/document.getElementById file-input-id)))}
     [:div.drop-zone-container
      [:i.fa.fa-paperclip.fa-lg]
      [:span.drop-zone-content (i18n/t :drag_files_here_or_click_to_select)]]
     [:input {:type      :file
              :multiple  true
              :on-change (fn [e]
                           (process-files position (-> e .-target .-files)))
              :style     {:visibility :hidden
                          :height     "8.1px"}
              :id        file-input-id}]]))

(defn et-form-title []
  (let [new? (re-frame/subscribe [:et/new?])
        title (re-frame/subscribe [:et/title])
        local-title (reagent/atom @title)
        editing? (reagent/atom false)]
    (fn []
      [:div.row
       [:div.col-12
        [:div.form-group
         [:h4.form-label.et-title.ellipsis
          (i18n/t :exploratory_test) " : "
          [:span {:title @title} @title]]]]])))

(defn multi-list-field [{:keys [id] :as field}]
  (let [values (map (fn [v]
                      {:value v :label v}) (:possible-values field))
        value @(re-frame/subscribe [:et/field (keyword id)])
        state (reagent/atom (or value
                                (:default-value field)
                                []))

        props {:placeholder "Select ..."
               :clearable?  true
               :multi?      true}
        on-change #(re-frame/dispatch [:et/set-field
                                       (keyword id)
                                       (vec (map (fn [v]
                                                   (:label v)) (js->clj % :keywordize-keys true)))])]
    [et-select values @state props on-change]))

(defn text-field [field]
  (let [state (reagent/atom nil)]
    (fn [field]
      [:input.form-control
       {:type          :text
        :default-value (or @(re-frame/subscribe [:et/field (keyword (:id field))])
                           (:default-value field)
                           "")
        :ref           (fn [input]
                         (reset! state input))
        :on-change     #(re-frame/dispatch [:et/set-field (keyword (:id field)) (.-value @state)])}])))

(defn update-annotation-filter-flag []
  (re-frame/dispatch [:et/change-annotations-filter-flag]))

(defn text-filter-field []
  [:input.form-control
   {:type      :text
    :value     @(re-frame/subscribe [:et/description-filter])
    :on-change (fn [e]
                 (let [description-filter (event->value e)]
                   (re-frame/dispatch [:et/set-description-filter description-filter])
                   (update-annotation-filter-flag)))}])

(defn picklist-filter-field []
  [:div.row
   [:div.col-10
    {:style {:margin-left 27}}
    [:select.form-control
     {:value     @(re-frame/subscribe [:et/type-filter])
      :on-change (fn [e]
                   (let [type-filter (event->value e)]
                     (re-frame/dispatch [:et/set-type-filter type-filter])
                     (update-annotation-filter-flag)))}
     (for [[i at] (map-indexed vector (cons {:id nil :label "" :color nil} @(re-frame/subscribe [:et/annotation-types])))]
       ^{:key i} [:option {:value (:id at)} (:label at)])]]])

(defn memo-field [field]
  (let [state (reagent/atom nil)]
    (fn [field]
      [:textarea.form-control
       {:rows          5
        :cols          20
        :default-value (or @(re-frame/subscribe [:et/field (keyword (:id field))])
                           (:default-value field)
                           "")
        :ref           (fn [input]
                         (reset! state input))
        :on-change     #(re-frame/dispatch [:et/set-field (keyword (:id field)) (.-value @state)])}])))

(defn url-field [field]
  (let [value (re-frame/subscribe [:et/field (keyword (:id field))])
        state (reagent/atom {:value @value :editing? (not (boolean @value))})]
    (fn [field]
      (if (:editing? @state)
        [:div.input-group
         [:input.form-control
          {:type      :url
           :value     (:value @state)
           :on-change #(swap! state assoc :value (-> % .-target .-value))}]
         [:div.input-group-btn
          [:button.btn.btn-light
           {:type     :button
            :style {:border "1px solid var(--pt-color-border-light)"}
            :on-click (fn [_]
                        (re-frame/dispatch [:et/set-field
                                            (keyword (:id field))
                                            (:value @state)])
                        (swap! state assoc :editing? false))}
           [:i.fa.fa-check]]
          [:button.btn.btn-light
           {:type     :button
            :style {:border "1px solid var(--pt-color-border-light)"}
            :on-click #(reset! state {:value @value :editing? (not (boolean @value))})}
           [:i.fa.fa-close]]]]
        [:div.input-group
         [:p.form-control-static.ellipsis
          [:a {:href   @value
               :target "_blank"
               :title  @value}
           @value]]
         [:div.input-group-btn
          [:button.btn.btn-light
           {:type     :button
            :style {:border "1px solid var(--pt-color-border-light)"}
            :on-click #(swap! state assoc :editing? true)}
           [:i.fa.fa-pencil]]]]))))

(defn date-field [field]
  (let [value (or @(re-frame/subscribe [:et/field (keyword (:id field))]) "")]
    [datepicker2/date-picker-input
     {:value     value
      :time?     false
      :class     "m-input form-control"
      :on-change #(re-frame/dispatch [:et/set-field (keyword (:id field)) %])}]))

(defn datetime-field [field]
  (let [value @(re-frame/subscribe [:et/field (keyword (:id field))])]
    [datepicker/instant-picker
     {:value     value
      :class     "m-input form-control"
      :field-id  (:id field)
      :on-change #(re-frame/dispatch [:et/set-field (keyword (:id field)) %])}]))

(defn checkbox-field [field]
  [:div.form-check
   [:input.form-check-input
    {:type      :checkbox
     :checked   (parse-boolean (or @(re-frame/subscribe [:et/field (keyword (:id field))])
                                   (:default-value field)
                                   nil))
     :on-change #(re-frame/dispatch [:et/set-field (keyword (:id field)) (-> % .-target .-checked)])}]])

(defn unsupported-field [field]
  [:input.form-control
   {:type     :text
    :disabled true
    :value    (or @(re-frame/subscribe [:et/field (keyword (:id field))]) "")}])

(defn version-label-field []
  [:input.form-control
   {:type      :text
    :disabled  true
    :read-only true
    :value     (or @(re-frame/subscribe [:et/version-label]) "")}])

(defn et-form-field [{:keys [id format mandatory name] :as field}]
  [:div {:class (if (= "memo" format) "col-6" "col-3")}
   [:div.form-group
    [field-label mandatory name]
    (condp = (keyword format)
      :user [user-field id
             (cons {:id "" :name ""} @(re-frame/subscribe [:et/users]))
             (:default-value field)
             @(re-frame/subscribe [:et/field (keyword id)])]
      :list [list-field id
             (cons {:id "" :name ""}
                   (map #(hash-map :id % :name %) (:possible-values field)))
             (:default-value field)
             @(re-frame/subscribe [:et/field (keyword id)])]
      :linkedlist (let [parent-value @(re-frame/subscribe [:et/field (keyword (:parent-id field))])]
                    [list-field id
                     (cons {:id "" :name ""}
                           (map #(hash-map :id % :name %)
                                (when parent-value
                                  (get-in field [:possible-values (keyword parent-value)]))))
                     (:default-value field)
                     @(re-frame/subscribe [:et/field (keyword id)])])
      :multilist [multi-list-field field]
      :text [text-field field]
      :number [text-field field]
      :date [date-field field]
      :datetime [datetime-field field]
      :checkbox [checkbox-field field]
      :memo [memo-field field]
      :url [url-field field]
      :version-label [version-label-field]
      [unsupported-field field])]])

(defn partition-fields [fields]
  ;; return rows
  ;; each row will contain from 2  to 4 fields
  ;; each field takes either 3 or 6 slots of the bootstrap grid
  (letfn [(field-size [f]
            (if (= "memo" (:format f)) 6 3))
          (row-size [row]
            (reduce + (map field-size row)))]
    (loop [rows [[]] fields fields]
      (if (empty? fields)
        rows
        (let [cf (first fields)]
          (if (> (+ (row-size (last rows)) (field-size cf)) 12)
            (recur (conj rows [cf]) (rest fields))
            (recur (conj (vec (butlast rows)) (conj (last rows) cf)) (rest fields))))))))

(defn et-form-preconditions []
  (let [preconditions (re-frame/subscribe [:et/preconditions])]
    (when-not (empty? @preconditions)
      [:div.row
       [:div.col-12
        [:b (i18n/t :preconditions)] ":"]
       [:div.col-12.et-preconditions-text
        {:dangerouslySetInnerHTML {:__html @preconditions}}]])))

(defn et-form-fields []
  (let [fields (clj->js @(re-frame/subscribe [:et/fields]))
        rows (partition-fields fields)
        versioning-enabled? @(re-frame/subscribe [:et/versioning-enabled?])]
    [:div.row
     [:div.col-12.et-form-fields
      (for [[i row] (map-indexed vector rows)]
        ^{:key i} [:div.row
                   {:style {:margin-bottom "10px"}}
                   (for [[i field] (map-indexed vector row)]
                     ^{:key i} [et-form-field (js->clj field :keywordize-keys true)])])
      (when versioning-enabled?
        [:div.row
         {:style {:margin-bottom "10px"}}
         [et-form-field {:id "version-label"
                         :format "version-label"
                         :mandatory false
                         :name (get-translation "test.versioning.version-label" "Version Label")}]])
      [et-form-preconditions]]]))

(defn et-form-specific []
  [:div.row.pb-2
   [et-memo-field (i18n/t :test_charter) (i18n/t :test_charter) true [:et/charter] (fn [value] [:et/set-charter value])]
   [et-memo-field (i18n/t :guide_points) (i18n/t :guide_points) false [:et/guide-points] (fn [value] [:et/set-guide-points value])]])

(defn et-form-duration []
  [:div.row
   [:div.col-4.et-title
    [:label (i18n/t :estimate)]]
   [:div.col-8
    [:input.form-control
     {:type      :text
      :read-only true
      :value     @(re-frame/subscribe [:et/estimate])
      :on-change #(re-frame/dispatch [:et/set-estimate (event->value %)])}]]])

(defn et-form-run-status []
  (let [value @(re-frame/subscribe [:et/run-status])
        run-statuses (map-indexed vector @(re-frame/subscribe [:et/run-statuses]))
        values (map (fn [v] {:value (first v) :label (last v)}) run-statuses)
        value (if (string? value)
                {:value (:value (first (filter #(= (:label %) value) values))) :label value}
                value)
        state (reagent/atom (or value ""))
        on-change #(re-frame/dispatch [:et/set-run-status
                                       (:label (js->clj % :keywordize-keys true))])]
    [:div.row
     [:div.col-4.et-title
      [:label.form-label (i18n/t :run_status)]]
     [:div.col-8
      {:style {:max-height "60px"}}
      [et-select values @state {} on-change]]]))

(defn et-form-last-run []
  [:div.row
   [:div.col-4.et-title
    [:label (i18n/t :last_run)]]
   [:div.col-6
    [:p.form-control-static
     {:style {:margin "8px 0"}}
     @(re-frame/subscribe [:et/last-run])]]])

(defn et-form-tester []
  [:div.row
   [:div.col-4.et-title
    [:label (i18n/t :tester)]]
   [:div.col-6
    [:p.form-control-static
     {:style {:margin "8px 0"}}
     @(re-frame/subscribe [:et/tester])]]])

(defn et-form-running-time []
  (let [running-time (re-frame/subscribe [:et/running-time])
        running? (re-frame/subscribe [:et/running?])
        state (reagent/atom @running-time)
        update-rt (fn []
                    (if-let [duration (utils/parse-duration* @state)]
                      (do
                        (log/info :et-form-running-time :update-rt duration)
                        (re-frame/dispatch [:et/set-running-time duration])
                        (reset! state (utils/format-duration duration)))
                      (reset! state @running-time)))]
    (fn []
      [:div.row
       [:div.col-12
        [:div.row
         [:div.col-4.et-title
          [:label (i18n/t :running_time)]]
         [:div.col-8
          [:div.input-group
           (if @running?
             [:input.form-control
              {:type      :text
               :read-only true
               :tab-index -1
               :value     @running-time
               :on-change #(log/warn (i18n/t :called_on_change_ro))}]
             [:input.form-control
              {:type      :text
               :value     @state
               :on-change #(reset! state (event->value %))
               :on-key-up (fn [e]
                            (when (= 13 (.-keyCode e))
                              (update-rt)))
               :on-blur   update-rt}])
           [:div.input-group-btn.ui-editable-buttons
            (if @running?
              [:button.btn.btn-primary
               {:type     :button
                :on-click (fn [_]
                            (reset! state @running-time)
                            (re-frame/dispatch [:et/pause-running-timer]))}
               [:i.fa.fa-pause.fa-fw]]
              [:button.btn.btn-primary
               {:type     :button
                :on-click #(re-frame/dispatch [:et/start-running-timer])}
               [:i.fa.fa-play.fa-fw]])]]]]
        [:div.row
         [:div.col-12
          [:p.et-idle-timer @(re-frame/subscribe [:et/idle-message])]]]]])))

(defn et-form-actions []
  (let [new? (re-frame/subscribe [:et/new?])
        loading? (re-frame/subscribe [:et/loading?])
        entity (re-frame/subscribe [:et/entity])]
    (fn []
      (letfn [(disabled? []
                (boolean
                  (or @loading?
                      (and (not (dirty? @entity))
                           (not-any? dirty? (:annotations @entity))))))]
        [:div.row.flex-end
         [:div.col-7.et-form-actions
          {:class (when-not (disabled?) "changedInput")}
          (if @new?
            [:div.btn-group
             [:button.btn.btn-light
              {:type     :button
               :disabled (disabled?)
               :on-click #(re-frame/dispatch [:et/create :run])}
              (if @loading?
                [:span [:i.fa.fa-cog.fa-spin.fa-fw] (i18n/t :please_wait)]
                [:span [:i.fa.fa-save.fa-fw] (i18n/t :save_changes)])]
             [:button.btn.btn-default.dropdown-toggle
              {:type          :button
               :disabled      (disabled?)
               :data-bs-toggle   (i18n/t :dropdown)
               :aria-haspopup true
               :aria-expanded false}
              [:span.caret]
              [:span.sr-only (i18n/t :toggle_dropdown)]]
             [:ul.dropdown-menu
              [:li [:a
                    {:href     "#"
                     :on-click (fn [e]
                                 (.preventDefault e)
                                 (re-frame/dispatch [:et/create :close]))}
                    [:i.fa.fa-save.fa-fw] (i18n/t :save_and_close)]]]]
            [:div.btn-group
             [:button.btn.btn-light
              {:type     :button
               :style {:border    "1px solid var(--pt-color-border-light)"}
               :disabled (disabled?)
               :on-click #(re-frame/dispatch [:et/update])}
              (if @loading?
                [:span [:i.fa.fa-cog.fa-spin.fa-fw] (i18n/t :please_wait)]
                [:span [:i.fa.fa-save.fa-fw] (i18n/t :save_changes)])]
             [:button.btn.btn-light.dropdown-toggle
              {:type          :button
               :style {:border    "1px solid var(--pt-color-border-light)"}
               :data-bs-toggle   (i18n/t :dropdown)
               :aria-haspopup true
               :aria-expanded false}
              [:span.caret]
              [:span.sr-only (i18n/t :toggle_dropdown)]]
             [:ul.dropdown-menu
              [:li [:a
                    {:href @(re-frame/subscribe [:et/new-test-from-et-url])}
                    [:i.fa.fa-list.fa-fw] (str " " (i18n/t :create_scripted_test))]]
              [:li [:a
                    {:href     "#"
                     :on-click (fn [e]
                                 (.preventDefault e)
                                 (re-frame/dispatch [:et/delete]))}
                    [:i.fa.fa-times.fa-fw] (i18n/t :delete)]]]])]]))))

(defn report-internal-issue-button [annotation-position]
  (let [report-issue-url (re-frame/subscribe [:et/report-issue-url annotation-position])]
    (fn [annotation-position]
      [:a.inline-text {:href @report-issue-url :tab-index -1}
       [:i.fa.fa-thumbs-down.fa-fw] (i18n/t :report_issue)])))

(defn- open-bugtracker-modal [annotation-position]
  (let [title @(re-frame/subscribe [:et/title])
        issue-name (str (i18n/t :bug_reported_from_et) title)
        annotation-id @(re-frame/subscribe [:et/annotation-id annotation-position])
        issue-description @(re-frame/subscribe [:et/annotation-description annotation-position])]
    (re-frame/dispatch [::events/open-bugtracker-modal! annotation-id
                         {:issue-name issue-name
                          :issue-description issue-description
                          :params {:et_annotation_id annotation-id
                                   :entity_type nil}}])))

(defn report-modal-external-issue-button [annotation-position]
  [:a.inline-text {:href     "#"
                   :on-click (fn [e]
                               (.preventDefault e)
                               (open-bugtracker-modal annotation-position))}
   [:i.fa.fa-thumbs-down.fa-fw] (i18n/t :report_issue)])

(defn report-external-issue-button [annotation-position]
  (let [report-issue-url (re-frame/subscribe [:et/report-issue-url annotation-position])
        integration-options (re-frame/subscribe [:et/integration-options])]
    (fn [annotation-position]
      (cond
        (empty? @integration-options)
        [:a.inline-text {:href      @report-issue-url
                         :tab-index -1
                         :target    "_blank"}
         [:i.fa.fa-thumbs-down.fa-fw] (i18n/t :report_issue)]

        (= 1 (count @integration-options))
        [:a.inline-text {:href      (str @report-issue-url "?selected=" (ffirst @integration-options))
                         :tab-index -1
                         :target    "_blank"}
         [:i.fa.fa-thumbs-down.fa-fw] (i18n/t :report_issue)]

        :else
        [:div.dropdown
         [:a.dropdown-toggle.inline-text {:href          "#"
                                          :data-toggle   "dropdown"
                                          :aria-haspopup true
                                          :aria-expanded false}
          [:i.fa.fa-thumbs-down.fa-fw] (i18n/t :report_issue)]
         (into [:ul.dropdown-menu
                [:li.dropdown-header
                 (i18n/t :external_issue_choose_project)]]
               (for [[selected label] @integration-options]
                 [:li [:a {:href      (str @report-issue-url "?selected=" selected)
                           :tab-index -1
                           :target    "_blank"}
                       label]]))]))))

(defn et-annotation-action-report-issue [annotation-position]
  (let [dirty? (re-frame/subscribe [:et/dirty-annotation? annotation-position])
        integrations-enabled? (re-frame/subscribe [:et/integrations-enabled?])
        modal-integration-settings (re-frame/subscribe [:et/modal-integration-settings])
        current-project (re-frame/subscribe [::subs/current-project])]
    (fn [annotation-position]
      (let [bugtracker (bugtracker/bugtracker-type @current-project)
            sync-disabled? (and (bugtrackers/jira? bugtracker)
                                (not (:skip_mapping_in_manual_import @current-project))
                                (not (:issues_sync_enabled @current-project true)))]
        [:div.report-btn-container
         (if @dirty?
           [:a.inline-text {:href      "#"
                            :tab-index -1
                            :on-click  (fn [e]
                                         (.preventDefault e)
                                         (re-frame/dispatch [:et/update])
                                         nil)}
            [:i.fa.fa-save.fa-fw] (i18n/t :save_to_report_issue)]
           (if sync-disabled?
             [:a.inline-text.disabled {:href      "#"
                                       :tab-index -1
                                       :title     (get-translation "entity.dropdown.sync_disabled_tooltip"
                                                                   "This option has been disabled by your administrator's settings.")}
              [:i.fa.fa-thumbs-down.fa-fw] (i18n/t :report_issue)]
             (if @integrations-enabled?
               (if @modal-integration-settings
                 [report-modal-external-issue-button annotation-position]
                 [report-external-issue-button annotation-position])
               [report-internal-issue-button annotation-position])))]))))

(defn et-annotation-action-link-existing-issue [annotation-position]
  (let [id (re-frame/subscribe [:et/annotation-id annotation-position])
        linking-existing (re-frame/subscribe [:et/linking-existing])
        integration-settings (re-frame/subscribe [:et/modal-integration-settings])
        state (reagent/atom "")]
    (fn [annotation-position]
      (when-not (nil? @id)
        [:div.report-btn-container
         (if (contains? @linking-existing @id)
           [:<>
            [:div.input-group.input-group-sm.col-12
             [ui/initial-focus-wrapper
              [:input.form-control.col-5
               {:type         :text

                :placeholder  (i18n/t :issue_id)
                :value        @state
                :on-change    #(reset! state (-> % .-target .-value))
                :on-key-press (fn [e]
                                (if
                                  (= 13 (.-charCode e))
                                  (re-frame/dispatch [:et/link-existing annotation-position @state])))}]]
             [:div.input-group-btn.col-7.flex-row
              [:button.btn.btn-light.col-6.et-annotation-action-buttons
               {:type     :button
                :disabled (empty? @state)
                :on-click (fn [e]
                            (log/info :et-annotation-action-link-existing-issue :link)
                            (re-frame/dispatch [:et/link-existing annotation-position @state]))}
               [:i.fa.fa-link]]
              [:button.btn.btn-light.col-6.et-annotation-action-buttons
               {:type     :button
                :on-click (fn [e]
                            (log/info :et-annotation-action-link-existing-issue :cancel)
                            (re-frame/dispatch [:et/cancel-linking-existing @id]))}
               [:i.fa.fa-close]]]]
            (when (seq (get-in @linking-existing [@id :errors]))
              (into [:<>]
                    (for [error (get-in @linking-existing [@id :errors])]
                      [:div.alert.alert-danger error])))
            (when (seq (get-in @linking-existing [@id :messages]))
              (into [:<>]
                    (for [[message f] (get-in @linking-existing [@id :messages])]
                      [:div.alert.alert-warning
                       message
                       (when (:auto_import_non_existing @integration-settings)
                         [:a.alert-link
                          {:href     "#"
                           :on-click (fn [e]
                                       (.preventDefault e)
                                       (js-invoke js/_pt f))}
                          "click here"])])))]
           [:a.inline-text {:href      "#"
                            :tab-index -1
                            :on-click  (fn [e]
                                         (.preventDefault e)
                                         (log/info :et-annotation-action-link-existing-issue annotation-position @id)
                                         (re-frame/dispatch [:et/start-linking-existing @id])
                                         nil)}
            [:i.fa.fa-thumbs-down.fa-fw] (i18n/t :link_existing_issue)])]))))

(defn et-annotation-actions [n-annotations position]
  [:div.row
   [:div.col-8
    [:div.row [et-annotation-action-report-issue position]]
    [:div.row [et-annotation-action-link-existing-issue position]]]
   [:div.col-4.text-center.flex-row {:style {:column-gap "2px"}}
    [:div.btn-group-vertical {:role  "group"
                              :style {:row-gap          "2px"
                                      :background-color "var(--pt-color-white)"
                                      :justify-content "normal"}}
     [:button.btn.btn-light.btn-xs.et-annotation-action-buttons
      {:type      :button
       :disabled  (or (zero? position) @(re-frame/subscribe [:et/display-filters]))
       :tab-index -1
       :on-click  #(re-frame/dispatch [:et/move-annotation-up position])}
      [:i.fa.fa-arrow-up.fa-fw]]
     [:button.btn.btn-light.btn-xs.et-annotation-action-buttons
      {:type      :button
       :disabled  (or (= position (dec n-annotations)) @(re-frame/subscribe [:et/display-filters]))
       :tab-index -1
       :on-click  #(re-frame/dispatch [:et/move-annotation-down position])}
      [:i.fa.fa-arrow-down.fa-fw]]]
    [:div.btn-group-vertical {:role  "group"
                              :style {:row-gap          "2px"
                                      :background-color "var(--pt-color-white)"
                                      :justify-content "normal"}}
     [:button.btn.btn-light.btn-xs.et-annotation-action-buttons
      {:type      :button
       :tab-index -1
       :on-click  #(re-frame/dispatch [:et/remove-annotation position])}
      [:i.fa.fa-times.fa-fw]]
     [:button.btn.btn-light.btn-xs.et-annotation-action-buttons
      {:type     :button
       :disabled @(re-frame/subscribe [:et/display-filters])
       :on-focus #(re-frame/dispatch [:et/set-annotation-focus position])
       :on-click #(re-frame/dispatch [:et/add-annotation (inc position)])}
      [:i.fa.fa-plus.fa-fw]]]]])

(defn et-annotation-attachment [annotation-position [attachment-position attachment]]
  [:div.row.et-annotation-attachment.flex-row
   [:div.thumbnail.col-4
    (if (:url attachment)
      [:a {:target "_blank" :href (:url attachment) :tab-index -1}
       (build-preview attachment)]
      (build-preview attachment))]
   [:div.col-6.attachment-name-container
    [:div.row [:div.col-12 [:small (:name attachment)] [:b " | "] [:small (humanize-size (:size attachment))] [:b " | "] [:small (:type attachment)]]]]
   [:div
    [:a {:href     "#"
         :on-click (fn [e]
                     (.preventDefault e)
                     (re-frame/dispatch [:et/remove-annotation-attachment annotation-position attachment-position]))
         :style    {:color "var(--pt-color-black)"}}
     [:i.fa.fa-times.ms-2]]]])

(defn et-annotation-attachments [position attachments]
  [:div.col-12
   [:div.row (dropzone position)]
   [:div.row
    (into [:div.col-12]
          (map (partial et-annotation-attachment position)
               (map-indexed vector attachments)))]])

(defn toggle-display-filter [e]
  (.preventDefault e)
  (re-frame/dispatch [:et/set-display-filters (not @(re-frame/subscribe [:et/display-filters]))])
  (re-frame/dispatch [:et/set-type-filter ""])
  (re-frame/dispatch [:et/set-description-filter ""])
  (update-annotation-filter-flag))

(defn customize-annotation-type-infobox []
  [:span.pull-left.px-1
   [:span
    [common-tooltip/info-circle-tooltip " Customize annotation types in project settings. If you don't have permissions, ask your project administrator to customize annotation types."]]])

(defn et-form-annotations-header []
  [:div.row.et-bg-primary.et-annotation-header
   [:div.col-1 [:span "ID"]]
   [:div.col-2 [:span (i18n/t :type)
                [:i.fa.fa-filter.filter-icon.clickble
                 {:on-click (fn [e] (toggle-display-filter e))}]]
    [customize-annotation-type-infobox]]
   [:div.col-5
    [:span (i18n/t :description)
     [:i.fa.fa-filter.filter-icon.clickble
      {:on-click (fn [e] (toggle-display-filter e))}]]]
   [:div.col-2 [:span (i18n/t :attachments_urls)]]
   [:div.col-2 [:span (i18n/t :actions)]]])

(defn et-form-annotations-filter []
  [:div.col-12
   [:div.row.filters-row
    [:div.col-1]
    [:div.col-2 [:span [picklist-filter-field]]]
    [:div.col-5 [:span [text-filter-field]]]
    [:div.col-2]
    [:div.col-2]]])

(defn et-annotation-type [position value]
  [:select.form-control.et-annotation-select
   {:value     value
    :on-focus  #(re-frame/dispatch [:et/set-annotation-focus position])
    :on-change #(re-frame/dispatch [:et/set-annotation-type position (event->value %)])}
   (for [[i at] (map-indexed vector @(re-frame/subscribe [:et/annotation-types]))]
     ^{:key i} [:option {:value (:id at)} (:label at)])])

(defn et-annotation-linked-issue [annotation-position issue]
  [:li
   [:i.fa.fa-bug.fa-fw]
   (let [title (str (i18n/t :issue) (:display-id issue) " - " (:name issue) " (" (:status issue) ")")]
     (if (string/blank? (:url issue))
       [:span title]
       [:a (cond-> {:href (:url issue)}
             (re-find #"^https?://" (:url issue))
             (assoc :target "_BLANK"))
        title]))
   [:a {:href     "#"
        :on-click (fn [e]
                    (.preventDefault e)
                    (re-frame/dispatch [:et/remove-annotation-issue
                                        annotation-position issue])
                    nil)}
    [:i.fa.fa-trash-o.fa-fw]]])

(defn et-annotation-linked-issues [annotation position]
  (let [refreshing? (re-frame/subscribe [:et/refreshing-linked-issues? (:id annotation)])]
    (fn [annotation position]
      (when (seq (:issues annotation))
        [:div.row
         [:div.col-12
          [:h5 (i18n/t :linked_issues)
           [:span {:class (when @refreshing? "disabled")}
            [:a {:href     "#"
                 :on-click (fn [e]
                             (.preventDefault e)
                             (re-frame/dispatch [:et/refresh-linked-issues position])
                             nil)}
             (if @refreshing?
               [:i.fa.fa-refresh.fa-spin.fa-fw]
               [:i.fa.fa-refresh.fa-fw])]]]
          (into [:ul.list-unstyled]
                (for [issue (:issues annotation)]
                  [et-annotation-linked-issue position issue]))]]))))

(defn et-annotation-description [position placeholder subscription]
  (let [state (atom nil)
        markdown-support? (re-frame/subscribe [:et/markdown-support?])
        rtl? (re-frame/subscribe [:et/rtl?])
        set-value-event (fn [value] [:et/set-annotation-description position value])]
    (fn [position placeholder subscription]
      (do
        (when-let [input @state]
          (when-not (= (.-value input) subscription)
            (set! (.-value input) subscription)))
        (if markdown-support?
          [markdown-container placeholder false subscription set-value-event position false]
          [:textarea.form-control
           (cond-> {:rows          2
                    :cols          40
                    :default-value subscription
                    :on-focus      #(re-frame/dispatch [:et/set-annotation-focus position])
                    :ref           #(reset! state %)
                    :on-change     #(re-frame/dispatch [:et/set-annotation-description position (.-value @state)])}
             @rtl? (assoc :dir "rtl"))])))))

(defn et-annotation [[position n-annotations] _]
  (let [annotationa (re-frame/subscribe [:et/annotation position])]
    (fn [[position n-annotations] _]
      (let [annotation @annotationa
            display-filter @(re-frame/subscribe [:et/display-filters])]
        (when (:filter-display-flag annotation)
          [:div.row.et-annotation {:id    (:id annotation)
                                   :class (string/join " "
                                                       [(when (odd? position)
                                                          "et-annotation-odd")
                                                        (when (and (not display-filter) (:focus annotation))
                                                          "et-annotation-focus")])}
           [:div.row
            [:div.col-1
             [:span
              [:i.fa.fa-cog.fa-fw
               {:style {:color (if (dirty? annotation) "darkred" "black")}}]
              (str "#" (inc position))]]

            [:div.col-2.flex-row
             {:style {:column-gap "5px"}}
             [:div.et-annotation-type-cc
              {:style {:background-color (->> @(re-frame/subscribe [:et/annotation-types])
                                              (filter #(= (:type annotation) (:id %)))
                                              first
                                              :color)}}]
             (if (and (not display-filter) (:initial-focus annotation))
               [ui/initial-focus-wrapper
                [et-annotation-type position (:type annotation)]]
               [et-annotation-type position (:type annotation)])]

            [:div.col-5
             [et-annotation-description position (i18n/t :description) [:et/annotation-description position]]]
            [:div.col-2
             [et-annotation-attachments position (:attachments annotation)]]
            [:div.col-2
             [et-annotation-actions n-annotations position]]]
           [et-annotation-linked-issues annotation position]])))))

(defn et-form-annotations []
  [:div.row
   [:div.col-12.et-annotations-container
    [et-form-annotations-header]
    [:div.row.et-annotation
     (if @(re-frame/subscribe [:et/display-filters])
       [et-form-annotations-filter]
       [:div])]
    (let [annotations @(re-frame/subscribe [:et/annotations])
          add-annotation-handler (fn [] (re-frame/dispatch [:et/add-annotation]))
          n (count annotations)]
      (concat
        (for [[i a] (map-indexed vector annotations)]
          ^{:key i} [et-annotation [i n] a])
        [^{:key n}
         [:div.row.justify-content-center
          [keyboard-shortcuts/add-shortcut {:shortcut [:edit-et-test :add-et-annotation] 
                                            :handler add-annotation-handler}]
          [:div.col-auto.col-offset-5.et-add-annotation-button
           [:button#add-annotation.btn.btn-primary
            {:type     :button
             :disabled @(re-frame/subscribe [:et/display-filters])
             :on-click add-annotation-handler}
            [:span [:i.fa.fa-plus.fa-fw] (i18n/t :add_annotation)]]]]]))]])

(defn et-errors []
  (let [errors @(re-frame/subscribe [:et/errors])
        flattened-errors (flatten errors)]
    (when (seq flattened-errors)
      [:div.error-explanation
       [:h4 (str (count flattened-errors) " " (i18n/t :err_msg_no_save))]
       [:p (i18n/t :err_msg_problem_with_fields)]
       [:ul
        (for [[i error] (map-indexed vector flattened-errors)]
          ^{:key i} [:li error])]])))

(defn et-form-right []
  (let [new? (re-frame/subscribe [:et/new?])
        time-management? (re-frame/subscribe [:et/time-management?])]
    (fn []
      (cond-> [:div.col-3
               [et-form-actions]]
        (not @new?)
        (into [[:div.row [:legend (i18n/t :last_run)]]
               [et-form-run-status]
               [et-form-last-run]
               [:div.row [:legend (i18n/t :users)]]
               [et-form-tester]])

        @time-management?
        (into [[:div.row [:legend (i18n/t :duration)]]
               [et-form-duration]])

        (and @time-management? (not @new?))
        (into [[et-form-running-time]])))))

(defn et-form-next-tests []
  (let [nearby-tests-url (re-frame/subscribe [:et/nearby-tests-url])
        run-next-test-url (re-frame/subscribe [:et/run-next-test-url])
        testset-url (re-frame/subscribe [:et/back-to-testset-url])
        run-next-test-handler #(re-frame/dispatch [:et/run-next-test @run-next-test-url])]
    (fn []
      [:div.row
       [:div.col-md-12
        [:div.modal.fade
         {:id          "near_by_modal"
          :tab-index   "-1"
          :role        "dialog"
          :aria-hidden "true"}
         [:div.modal-dialog.modal-md
          [:div.modal-content
           [:div.modal-header
            {:style {:display "flex"
                     :flex-direction "row-reverse"}}
            [:button.btn-light.close
             {:type         :button
              :data-bs-dismiss "modal"
              :aria-label   "Close"}
             [:span {:aria-hidden             "true"
                     :dangerouslySetInnerHTML {:__html "&times;"}}]]
            [:h5.modal-title (i18n/t :near_by_tests_to_run)]]
           [:div.modal-body
            [:div.progress {:style {:margin-bottom 0}}
             [:div.progress-bar.progress-bar-warning.progress-bar-striped.active
              {:style         {:width "100%"}
               :role          "progressbar"
               :aria-valuenow "45"
               :aria-valuemin "0"
               :aria-valuemax "100"}]]]]]]
        
        (when @run-next-test-url 
          [keyboard-shortcuts/add-shortcut {:shortcut [:edit-et-test :run-next-test] 
                                            :handler run-next-test-handler}])
        (if @run-next-test-url
          [:button.btn.btn-success.et-btn-success-color.pull-left
           {:type     :button
            :on-click run-next-test-handler}
           [:i.fa.fa-play (str "  " (i18n/t :run_next_test))]]
          [:a.btn.btn-success.et-btn-success-color.pull-left
           {:href @testset-url}
           (i18n/t :back_to_testset)])
        (when @nearby-tests-url
          [:button.btn.btn-light.near_by_tests_js.pull-left.padding-left
           {:data-modal-id  "near_by_modal"
            :data-modal-url @nearby-tests-url
            :type           "submit"
            :name           "button"
            :style          {:margin-left "6px"
                             :border      "1px solid var(--pt-color-border-light)"}
            :on-click       (fn [e]
                              (let [current-modal (js/$ "#near_by_modal")]
                                (js-invoke current-modal "modal" "show")
                                (-> current-modal
                                    (js-invoke "find" ".modal-body")
                                    (js-invoke "load" @nearby-tests-url))))}
           [:i.fa.fa-list-alt] (str "  " (i18n/t :show_near_by_tests))])]])))

(defn et-form []
  [:div.col-12
   [:div.row.et-form
    [:div.col-9
     [et-errors]
     [et-form-title]
     [et-form-specific]
     [et-form-fields]]
    [et-form-right]]
   (when-not @(re-frame/subscribe [:et/new?])
     [et-form-annotations])
   [et-form-next-tests]
   [:div.alert-container-position.center
    (when-let [success-message @(re-frame/subscribe [:et/success-message])]
      (log/info "success-message" success-message)
      [:div.alert.alert-success.et-alert-success
       {:role "alert"}
       success-message])]])

(defn main-panel []
  (let [initialized? (re-frame/subscribe [:et/initialized?])]
     (.addEventListener
       js/document
       "open-import-bugtracker-tickets-modal"
       (fn [_]
           (re-frame/dispatch
             [::import-ticket-events/toggle-import-from-bugtracker-modal])))

       (fn []
           [:div.container-fluid.old-frontend-inner-container.et-main-panel
            [keyboard-shortcuts/enable-scope {:shortcuts-type :edit-et-test}]
            (if initialized?
              [et-form]
              [:p (i18n/t :initializing)])
            [bugtracker/bugtracker-integration-modal]

            [:div.entities-grid {:class (when (:expanded? false) "col")}
             [import-ticket/import-bugtracker-tickets-modal nil
              (import-ticket/import-bugtracker-tickets-modal-options {:current-entity :issue
                                                                      :grid-state (reagent/atom {})})]]])))
